/**
 * components/Onboarding/steps/ReviewStep.tsx
 * Review screen for onboarding completion
 *
 * Displays all configured onboarding data with edit navigation
 */

// 1. External dependencies
import React from 'react';
import { Edit } from 'lucide-react';

// 2. Internal absolute imports
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Chip,
  Accordion,
  AccordionItem
} from '@enfomentis/ui';
import { ASSET_CLASS_TYPE_DISPLAY_MAP } from '@/constants/assetClass';
import { formatAssetClassTypeDisplay } from '@/utils/assetClass';
import { BROKER_CATALOG_DATA } from '@/data/brokerCatalog';

// 3. Relative imports
import { useOnboardingData } from '../hooks/useOnboardingData';
import type { WizardStep } from '../hooks/useWizardNavigation';

/**
 * Props for ReviewStep component
 */
interface ReviewStepProps {
  /** Active wizard steps */
  activeSteps: WizardStep[];
  /** Navigate to a specific step index */
  goToStep: (index: number) => void;
}

/**
 * Review step component
 */
export const ReviewStep: React.FC<ReviewStepProps> = ({ activeSteps, goToStep }) => {
  const { draft, setReturnToScreen } = useOnboardingData();

  /**
   * Navigate to a specific screen for editing
   */
  const handleEdit = (screenId: string): void => {
    // Capture snapshot when editing account-details or playbook-builder (to detect changes)
    const captureSnapshot = screenId === 'account-details' || screenId === 'playbook-builder';
    setReturnToScreen('review', captureSnapshot);
    const targetIndex = activeSteps.findIndex(step => step.id === screenId);
    if (targetIndex !== -1) {
      goToStep(targetIndex);
    }
  };

  /**
   * Get broker name from catalog by ID
   */
  const getBrokerName = (brokerId?: string): string => {
    if (!brokerId) return 'Custom Broker';
    const broker = BROKER_CATALOG_DATA.find(b => b.id === brokerId);
    return broker?.name || 'Custom Broker';
  };

  /**
   * Format days of week for display
   */
  const formatDays = (days?: Array<'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday'>): string => {
    if (!days || days.length === 0) return 'All days';
    if (days.length === 7) return 'All days';
    return days.map(d => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(', ');
  };

  /**
   * Format time for display
   */
  const formatTime = (time: string, format: '12h' | '24h'): string => {
    const parts = time.split(':');
    const hours = parseInt(parts[0] || '0', 10);
    const minutes = parseInt(parts[1] || '0', 10);

    if (format === '24h') {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    }
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  if (!draft) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-text-secondary">Loading...</p>
      </div>
    );
  }

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full max-h-[100dvh] w-full flex flex-col"
    >
      <CardHeader>
        <div>
          <h3 className="text-large font-semibold text-text-primary">Review Your Configuration</h3>
          <p className="text-sm text-text-secondary">Review all your settings before completing onboarding</p>
        </div>
      </CardHeader>

      <CardBody>
        <div className="overflow-y-auto space-y-4 scrollbar-hide border-t border-default-50">
          {/* Account Details Section */}
          <Card shadow='none' className='border-1'>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <h4 className="text-md font-semibold text-text-primary">Account Details</h4>
                <p className="text-sm text-text-secondary">Your trading account configuration</p>
              </div>
              <Button
                size="sm"
                variant="flat"
                startContent={<Edit className="w-4 h-4" />}
                onPress={() => handleEdit('account-details')}
              >
                Edit
              </Button>
            </CardHeader>
            <CardBody className="space-y-3">
              <div>
                <p className="text-sm text-text-secondary">Account Name</p>
                <p className="text-base text-text-primary font-medium">{draft.account.accountName}</p>
              </div>
              {draft.account.accountDescription && (
                <div>
                  <p className="text-sm text-text-secondary">Description</p>
                  <p className="text-base text-text-primary">{draft.account.accountDescription}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-text-secondary">Account Type</p>
                <p className="text-base text-text-primary capitalize">{draft.account.accountType}</p>
              </div>
              {draft.account.selectedAssetClasses && draft.account.selectedAssetClasses.length > 0 && (
                <div>
                  <p className="text-sm text-text-secondary mb-2">Asset Classes</p>
                  <div className="flex flex-wrap gap-2">
                    {draft.account.selectedAssetClasses.map((asset) => (
                      <Chip key={asset} size="sm" variant="flat">
                        {formatAssetClassTypeDisplay(asset, ASSET_CLASS_TYPE_DISPLAY_MAP)}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Prop Firm Details (if applicable) */}
          {draft.account.accountType === 'prop' && draft.account.propFirmDetails && (
            <Card shadow='none' className='border-1'>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <h4 className="text-md font-semibold text-text-primary">Prop Firm Details</h4>
                  <p className="text-sm text-text-secondary">Your prop firm configuration</p>
                </div>
                <Button
                  size="sm"
                  variant="flat"
                  startContent={<Edit className="w-4 h-4" />}
                  onPress={() => handleEdit('prop-firm')}
                >
                  Edit
                </Button>
              </CardHeader>
              <CardBody className="space-y-3">
                <div>
                  <p className="text-sm text-text-secondary">Firm Name</p>
                  <p className="text-base text-text-primary font-medium">
                    {draft.account.propFirmDetails.firmName === 'other-custom'
                      ? draft.account.propFirmDetails.customFirmName
                      : draft.account.propFirmDetails.firmName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Account Stage</p>
                  <p className="text-base text-text-primary capitalize">
                    {draft.account.propFirmDetails.accountStage.replace(/-/g, ' ')}
                  </p>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Broker & Fees Section */}
          {draft.account.selectedBrokerId && (
            <Card shadow='none' className='border-1'>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <h4 className="text-md font-semibold text-text-primary">Broker & Fees</h4>
                  <p className="text-sm text-text-secondary">Your broker and fee configuration</p>
                </div>
                <Button
                  size="sm"
                  variant="flat"
                  startContent={<Edit className="w-4 h-4" />}
                  onPress={() => handleEdit('broker')}
                >
                  Edit
                </Button>
              </CardHeader>
              <CardBody className="space-y-3">
                <div>
                  <p className="text-sm text-text-secondary">Broker</p>
                  <p className="text-base text-text-primary font-medium">
                    {getBrokerName(draft.account.selectedBrokerId)}
                  </p>
                </div>
                {draft.account.feeConfiguration && (
                  <div>
                    <p className="text-sm text-text-secondary">Fee Configuration</p>
                    <p className="text-base text-text-primary capitalize">
                      {draft.account.feeConfiguration.choice === 'standard' ? 'Standard Fees' : 'Custom Configuration'}
                    </p>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Strategy Section */}
          {draft.trading?.strategy && (
            <Card shadow='none' className='border-1'>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <h4 className="text-md font-semibold text-text-primary">Trading Strategy</h4>
                  <p className="text-sm text-text-secondary">Your overall trading approach</p>
                </div>
                <Button
                  size="sm"
                  variant="flat"
                  startContent={<Edit className="w-4 h-4" />}
                  onPress={() => handleEdit('strategy-definition')}
                >
                  Edit
                </Button>
              </CardHeader>
              <CardBody className="space-y-3">
                <div>
                  <p className="text-sm text-text-secondary">Strategy Name</p>
                  <p className="text-base text-text-primary font-medium">{draft.trading.strategy.name}</p>
                </div>
                {draft.trading.strategy.description && (
                  <div>
                    <p className="text-sm text-text-secondary">Description</p>
                    <p className="text-base text-text-primary">{draft.trading.strategy.description}</p>
                  </div>
                )}
                {draft.trading.strategy.philosophy && (
                  <div>
                    <p className="text-sm text-text-secondary">Philosophy</p>
                    <p className="text-base text-text-primary">{draft.trading.strategy.philosophy}</p>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {/* Playbooks Section */}
          {draft.trading?.playbooks && draft.trading.playbooks.length > 0 && (
            <Card shadow='none' className='border-1'>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <h4 className="text-md font-semibold text-text-primary">Playbooks</h4>
                  <p className="text-sm text-text-secondary">{draft.trading.playbooks.length} playbook{draft.trading.playbooks.length !== 1 ? 's' : ''} configured</p>
                </div>
                <Button
                  size="sm"
                  variant="flat"
                  startContent={<Edit className="w-4 h-4" />}
                  onPress={() => handleEdit('playbook-builder')}
                >
                  Edit
                </Button>
              </CardHeader>
              <CardBody>
                <Accordion variant="bordered">
                  {draft.trading.playbooks.map((playbook) => (
                    <AccordionItem
                      key={playbook.tempId}
                      title={playbook.name}
                      subtitle={playbook.description || undefined}
                    >
                      <div className="space-y-3 pb-3">
                        {playbook.philosophy && (
                          <div>
                            <p className="text-sm font-medium text-text-secondary mb-1">Philosophy</p>
                            <p className="text-sm text-text-primary">{playbook.philosophy}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-text-secondary mb-1">Configuration</p>
                          <div className="text-sm text-text-primary space-y-1">
                            <p>• {playbook.entryModelIds.length} entry model{playbook.entryModelIds.length !== 1 ? 's' : ''}</p>
                            {playbook.criteriaIds && playbook.criteriaIds.length > 0 && (
                              <p>• {playbook.criteriaIds.length} criteria item{playbook.criteriaIds.length !== 1 ? 's' : ''}</p>
                            )}
                            {playbook.confluences && playbook.confluences.length > 0 && (
                              <p>• {playbook.confluences.length} confluence{playbook.confluences.length !== 1 ? 's' : ''}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardBody>
            </Card>
          )}

          {/* Risk Profiles Section */}
          {draft.trading?.riskConfiguration?.profiles && draft.trading.riskConfiguration.profiles.length > 0 && (
            <Card shadow='none' className='border-1'>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <h4 className="text-md font-semibold text-text-primary">Risk Profiles</h4>
                  <p className="text-sm text-text-secondary">{draft.trading.riskConfiguration.profiles.length} profile{draft.trading.riskConfiguration.profiles.length !== 1 ? 's' : ''} configured</p>
                </div>
                <Button
                  size="sm"
                  variant="flat"
                  startContent={<Edit className="w-4 h-4" />}
                  onPress={() => handleEdit('risk-configuration')}
                >
                  Edit
                </Button>
              </CardHeader>
              <CardBody>
                <Accordion variant="bordered">
                  {draft.trading.riskConfiguration.profiles.map((profile) => (
                    <AccordionItem
                      key={profile.tempId}
                      title={profile.name}
                      subtitle={profile.description || undefined}
                    >
                      <div className="space-y-3 pb-3">
                        <div>
                          <p className="text-sm font-medium text-text-secondary mb-1">Account Default Risk</p>
                          <p className="text-sm text-text-primary">
                            {profile.accountDefault.type === 'percentage'
                              ? `${(profile.accountDefault.value * 100).toFixed(2)}% of account`
                              : `$${(profile.accountDefault.value / 100).toFixed(2)} fixed`}
                          </p>
                        </div>
                        {profile.strategyOverrides && profile.strategyOverrides.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-text-secondary mb-1">Strategy Overrides</p>
                            <p className="text-sm text-text-primary">{profile.strategyOverrides.length} override{profile.strategyOverrides.length !== 1 ? 's' : ''}</p>
                          </div>
                        )}
                        {profile.playbookOverrides && profile.playbookOverrides.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-text-secondary mb-1">Playbook Overrides</p>
                            <p className="text-sm text-text-primary">{profile.playbookOverrides.length} override{profile.playbookOverrides.length !== 1 ? 's' : ''}</p>
                          </div>
                        )}
                      </div>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardBody>
            </Card>
          )}

          {/* Basic Settings Section */}
          {draft.personal && (
            <Card shadow='none' className='border-1'>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <h4 className="text-md font-semibold text-text-primary">Basic Settings</h4>
                  <p className="text-sm text-text-secondary">Display and schedule preferences</p>
                </div>
                <Button
                  size="sm"
                  variant="flat"
                  startContent={<Edit className="w-4 h-4" />}
                  onPress={() => handleEdit('basic-settings')}
                >
                  Edit
                </Button>
              </CardHeader>
              <CardBody className="space-y-3">
                <div>
                  <p className="text-sm text-text-secondary">Timezone</p>
                  <p className="text-base text-text-primary">{draft.personal.displayTimezone || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Time Format</p>
                  <p className="text-base text-text-primary">{draft.personal.timeFormat === '12h' ? '12-hour' : '24-hour'}</p>
                </div>
                <div>
                  <p className="text-sm text-text-secondary">Week Starts On</p>
                  <p className="text-base text-text-primary capitalize">{draft.personal.weekStartsOn}</p>
                </div>
                {draft.personal.tradingSessions && draft.personal.tradingSessions.length > 0 && (
                  <div>
                    <p className="text-sm text-text-secondary mb-2">Trading Sessions</p>
                    <div className="space-y-2">
                      {draft.personal.tradingSessions.map((session, index) => (
                        <div key={index} className="p-3 bg-default-50 rounded-md">
                          <p className="text-sm font-medium text-text-primary">{session.name}</p>
                          {session.description && (
                            <p className="text-xs text-text-secondary mt-0.5">{session.description}</p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2 text-xs text-text-secondary">
                            <span>{formatTime(session.start, draft.personal.timeFormat)} - {formatTime(session.end, draft.personal.timeFormat)}</span>
                            <span>•</span>
                            <span>{formatDays(session.daysOfWeek)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </div>
      </CardBody>
    </Card>
  );
};
